import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Card, CardContent, CircularProgress, Grid, IconButton, Typography } from '@mui/material'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import ReactECharts from 'echarts-for-react'
import moment from 'moment'
import 'moment/locale/fr'
import { groupBy, orderBy, round, sumBy } from 'lodash'
import ApplicationConf from 'conf/ApplicationConf'
import useFetch from 'common/hooks/useFetch'
import { CardTitle } from 'common/components/card/Card'
import { darkBlue } from 'app/theme'
import { STATUS } from 'common/constants/storeConstants'

const AGGREGATE = 'all'
const NB_SLICES = 5
const OTHERS_LABEL = 'Autres'

const formatNb = (nb) => Number(nb).toLocaleString('fr-FR')

const capitalize = (label) => `${label.charAt(0).toUpperCase()}${label.slice(1)}`

// Valeur encadrée de deux flèches pour passer à la valeur précédente / suivante.
const Stepper = ({ label, onPrevious, onNext, isNextDisabled = false, width }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #cfd6e4', borderRadius: '4px' }}>
        <IconButton size='small' onClick={onPrevious}><ChevronLeft fontSize='small' /></IconButton>
        <Typography sx={{ width, textAlign: 'center', fontSize: 14, fontWeight: 600 }}>{label}</Typography>
        <IconButton size='small' onClick={onNext} disabled={isNextDisabled}><ChevronRight fontSize='small' /></IconButton>
    </Box>
)
Stepper.propTypes = {
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onPrevious: PropTypes.func,
    onNext: PropTypes.func,
    isNextDisabled: PropTypes.bool,
    width: PropTypes.number,
}

// Total de chaque valeur de `key` (serveur ou prison) : la ligne agrégée « all » sur l'autre dimension (ex. serveur X /
// jail all = total du serveur X) si elle existe, sinon la somme de ses lignes de détail. La ligne globale « all » de
// `key` elle-même est écartée. Les NB_SLICES plus gros sont gardés, le reste regroupé dans « Autres ».
const getSlices = (data, key, otherKey) => {
    const totals = Object.entries(groupBy(data.filter(d => d[key] !== AGGREGATE), key)).map(([name, group]) => {
        const totalRow = group.find(d => d[otherKey] === AGGREGATE)
        return { name, value: totalRow ? totalRow.nb : sumBy(group, 'nb') }
    })
    const sorted = orderBy(totals.filter(s => s.value > 0), 'value', 'desc')
    const others = sorted.slice(NB_SLICES)
    return others.length ? [...sorted.slice(0, NB_SLICES), { name: OTHERS_LABEL, value: sumBy(others, 'value'), details: others }] : sorted
}

const NB_OTHERS_DETAILS = 10

// Infobulle d'une part ; pour « Autres », détail des NB_OTHERS_DETAILS premiers éléments regroupés.
const formatTooltip = ({ name, value, percent, data }) => {
    const header = `<b>${name}</b> : ${formatNb(value)} (${percent} %)`
    if (!data?.details) {
        return header
    }
    // Pourcentage de l'élément sur le total du camembert, déduit de celui de « Autres ».
    const lines = data.details.slice(0, NB_OTHERS_DETAILS).map(d => `${d.name} : ${formatNb(d.value)} (${round(d.value / value * percent, 2)} %)`)
    const rest = data.details.length - NB_OTHERS_DETAILS
    return [header, ...lines, ...(rest > 0 ? [`… et ${rest} autre${rest > 1 ? 's' : ''}`] : [])].join('<br/>')
}

const Pie = ({ title, slices }) => {
    const option = useMemo(() => ({
        tooltip: { trigger: 'item', formatter: formatTooltip },
        series: [{
            type: 'pie',
            radius: ['0%', '60%'],
            data: slices,
            label: { formatter: '{b}\n{d} %' },
        }],
    }), [slices])
    return (
        <>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1c2340', textAlign: 'center' }}>{title}</Typography>
            <ReactECharts option={option} style={{ height: 300 }} notMerge />
        </>
    )
}
Pie.propTypes = {
    title: PropTypes.string,
    slices: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, value: PropTypes.number })),
}

// Répartition en % des bans d'un mois par serveur et par prison.
const Fail2banDetailChart = () => {
    const currentMonth = moment().startOf('month')
    const [period, setPeriod] = useState(() => currentMonth.clone().subtract(1, 'month'))

    // On ne va pas au-delà du mois en cours.
    const move = (amount, unit) => setPeriod(p => moment.min(p.clone().add(amount, unit), currentMonth))

    const year = period.year()
    const month = period.month() + 1
    const { data = [], status } = useFetch(ApplicationConf.supervision.fail2banDetail(year, month))

    const serveurSlices = useMemo(() => getSlices(data, 'serveur', 'jail'), [data])
    const jailSlices = useMemo(() => getSlices(data, 'jail', 'serveur'), [data])
    const hasDetail = serveurSlices.length > 0 || jailSlices.length > 0

    return (
        <Card sx={{ height: '100%' }}>
            <CardTitle title='Fail2ban — répartition des bans' color={darkBlue} />
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Stepper
                        label={year}
                        width={50}
                        onPrevious={() => move(-1, 'year')}
                        onNext={() => move(1, 'year')}
                        isNextDisabled={year >= currentMonth.year()}
                    />
                    <Stepper
                        label={capitalize(period.clone().locale('fr').format('MMMM'))}
                        width={90}
                        onPrevious={() => move(-1, 'month')}
                        onNext={() => move(1, 'month')}
                        isNextDisabled={!period.isBefore(currentMonth)}
                    />
                </Box>
                {status === STATUS.LOADING && data.length === 0 && <CircularProgress size={24} />}
                {status === STATUS.REJECTED && <Typography sx={{ fontSize: 13, color: '#c62828' }}>Impossible de charger le détail fail2ban</Typography>}
                {status === STATUS.SUCCEEDED && !hasDetail && (
                    <Typography sx={{ fontSize: 13, color: '#98a3b8' }}>Aucun détail par serveur / prison pour ce mois</Typography>
                )}
                {hasDetail && (
                    <Grid container spacing={1}>
                        <Grid size={6}><Pie title='Par serveur' slices={serveurSlices} /></Grid>
                        <Grid size={6}><Pie title='Par prison' slices={jailSlices} /></Grid>
                    </Grid>
                )}
            </CardContent>
        </Card>
    )
}

export default Fail2banDetailChart
